import {DateObject} from 'react-multi-date-picker'
import gregorian from 'react-date-object/calendars/gregorian'
import persian from 'react-date-object/calendars/persian'

export const normalizeDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))

const gregorianPartsForPersianDate = (value: string) => {
  const normalized = normalizeDigits(value.trim()).replace(/-/g, '/')
  if (!/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(normalized)) {
    throw new Error('تاریخ را با قالب ۱۴۰۵/۰۵/۱۳ وارد کنید.')
  }

  const [year, month, day] = normalized.split('/').map(Number)
  const monthLengths = persian.getMonthLengths(persian.isLeap(year))
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > monthLengths[month - 1]) {
    throw new Error('تاریخ شمسی معتبر نیست.')
  }
  const date = new DateObject({calendar: persian, year, month, day})
  if (!date.isValid) throw new Error('تاریخ شمسی معتبر نیست.')
  const converted = date.convert(gregorian)
  return {year: converted.year, month: converted.month.number, day: converted.day}
}

const timeZoneOffsetAt = (epoch: number, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(epoch))
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  const asUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  )
  return asUtc - Math.floor(epoch / 1000) * 1000
}

const tehranEpoch = (value: string, endOfDay: boolean) => {
  const {year, month, day} = gregorianPartsForPersianDate(value)
  const milliseconds = endOfDay ? 999 : 0
  const target = Date.UTC(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    milliseconds
  )
  let epoch = target
  for (let index = 0; index < 2; index += 1) {
    epoch = target - timeZoneOffsetAt(epoch, 'Asia/Tehran')
  }
  return epoch
}

export const persianDayToTehranRange = (value: string) => ({
  issuanceDateFrom: tehranEpoch(value, false),
  issuanceDateTo: tehranEpoch(value, true),
})

export const persianDateToTehranEpoch = (value: string, endOfDay = false) =>
  tehranEpoch(value, endOfDay)

export const todayPersian = (now: Date | number = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US-u-ca-persian', {
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(new Date(now))
    .reduce<Record<string, string>>((values, part) => {
      if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
        values[part.type] = normalizeDigits(part.value)
      }
      return values
    }, {})
  return `${parts.year}/${parts.month}/${parts.day}`
}

/** `value` as a zero-padded YYYY/MM/DD when it is a real Jalali day, otherwise ''. */
export const validPersianDay = (value?: string | null) => {
  const normalized = normalizeDigits((value || '').trim()).replace(/-/g, '/')
  if (!normalized) return ''
  try {
    gregorianPartsForPersianDate(normalized)
  } catch {
    return ''
  }
  const [year, month, day] = normalized.split('/')
  return `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`
}

/** Whether a Jalali day comes after today in Tehran. */
export const isPersianDayAfterToday = (value: string, now: Date | number = new Date()) => {
  const day = validPersianDay(value)
  return Boolean(day) && day > todayPersian(now)
}

/**
 * The first day the workspace searches: period 140404 (tp.tax.gov.ir/docs 2.0, TSP-API invoice
 * searches, «حداقل مجاز ... 140404»), checked by the server too (backend 712).
 */
export const FIRST_WORKSPACE_DAY = '1404/10/01'

export const isPersianDayBeforeFirstWorkspaceDay = (value: string) => {
  const day = validPersianDay(value)
  return Boolean(day) && day < FIRST_WORKSPACE_DAY
}

/**
 * The organisation requires the issuance day to belong to the requested period
 * (Jalali year + quarter); observed live as a 406 on 1405/06/22.
 */
export const isPersianDateInTaxPeriod = (value: string, period: string) => {
  const normalizedPeriod = normalizeDigits(period || '').trim()
  if (!/^\d{4}0[1-4]$/.test(normalizedPeriod)) return false
  const normalized = normalizeDigits((value || '').trim()).replace(/-/g, '/')
  const [year, month] = normalized.split('/').map(Number)
  if (!year || !month || month < 1 || month > 12) return false
  return (
    year === Number(normalizedPeriod.slice(0, 4)) &&
    Math.ceil(month / 3) === Number(normalizedPeriod.slice(4))
  )
}

export const currentTaxPeriod = (now: Date | number = new Date()) => {
  const [year, month] = todayPersian(now).split('/').map(Number)
  return `${year}${String(Math.ceil(month / 3)).padStart(2, '0')}`
}

/**
 * The period (Jalali year + quarter) an issuance day belongs to, e.g. 1405/07/01 → 140503.
 * The organisation only accepts a day inside the requested period, so the panel derives
 * the period from the day instead of asking for both (2026-09-27).
 */
export const taxPeriodOfPersianDate = (value: string) => {
  const normalized = normalizeDigits((value || '').trim()).replace(/-/g, '/')
  const match = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(normalized)
  if (!match) return ''
  const month = Number(match[2])
  if (month < 1 || month > 12) return ''
  return `${match[1]}${String(Math.ceil(month / 3)).padStart(2, '0')}`
}

const SEASONS = ['بهار', 'تابستان', 'پاییز', 'زمستان']

/** 140503 → «پاییز ۱۴۰۵». */
export const formatTaxPeriod = (period: string) => {
  const normalized = normalizeDigits(period || '')
  if (!/^\d{4}0[1-4]$/.test(normalized)) return period || '—'
  const year = Number(normalized.slice(0, 4)).toLocaleString('fa-IR', {useGrouping: false})
  return `${SEASONS[Number(normalized.slice(4)) - 1]} ${year}`
}

/** The Persian day `days` days after (negative: before) `value`, as YYYY/MM/DD. */
export const shiftPersianDay = (value: string, days: number) => {
  const tehranNoon = persianDateToTehranEpoch(value, false) + 12 * 3600 * 1000
  return todayPersian(tehranNoon + days * 86400 * 1000)
}

export const formatPersianDateTime = (value: unknown, withTime = true) => {
  if (value === null || value === undefined || value === '') return '—'
  const numeric = typeof value === 'number' ? value : Number(value)
  const parsed = Number.isFinite(numeric) ? new Date(numeric) : new Date(String(value))
  if (Number.isNaN(parsed.getTime())) return String(value)
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? {hour: '2-digit', minute: '2-digit'} : {}),
  }).format(parsed)
}

export const formatRemainingTime = (expiresAt: number | null, now = Date.now()) => {
  if (!expiresAt || expiresAt <= now) return 'پایان یافته'
  const totalSeconds = Math.ceil((expiresAt - now) / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toLocaleString('fa-IR')}:${String(seconds).padStart(2, '0')}`
}
